import {
  ActionType,
  initialState,
  reducer,
  updateEmail,
  useAuthStore,
} from "@kaplan-labs/up-auth-api-client";
import { EnvelopeSimpleIcon } from "@phosphor-icons/react/dist/ssr/EnvelopeSimple";
import { HTTPError } from "ky";
import { useReducer } from "react";
import { useForm } from "react-hook-form";

import { logError, useTheme } from "@up/data";
import { Check, Spinner, Toast, ToastTitle } from "@up/ui";

import { Email, OTP } from "./edit-profile-fields";

import type { TFirstTimeUser, TProfile } from "@up/data";

const BASE_URL = process.env["NEXT_PUBLIC_HALL_OF_RECORDS_URI"];

const ERROR_MESSAGES: Record<string, string> = {
  EmailAlreadyInUseError:
    "This email address is already associated with another account.",
  EmailClaimedInContactsError:
    "This email address is already associated with another account.",
  OtpExpiredError: "Your verification code has expired. Please request a new one.",
  ValidationError: "Please check your input and try again.",
  UserNotFoundError: "We couldn't find an account with that email address.",
  AuthError: "Authentication failed.",
};

function getErrorMessage(error: unknown): string {
  if (error instanceof HTTPError) {
    const body = error.data as
      | { error: { type: string; message: string } }
      | undefined;
    if (body?.error) {
      return (
        ERROR_MESSAGES[body.error.type] ??
        body.error.message ??
        error.message
      );
    }
  }
  return "An unexpected error occurred.";
}

type FormFields = {
  email: string;
  otp?: string;
  accessToken?: string;
};

export function EditProfileEmail({
  profile,
}: {
  profile: TProfile | TFirstTimeUser;
}) {
  const theme = useTheme();
  const auth = useAuthStore();

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    email: profile.email,
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      email: profile.email,
      otp: "",
      accessToken: "",
    },
  });

  const formValues = watch();

  const reset = function () {
    dispatch({
      type: ActionType.RESET,
    });
    setValue("otp", "");
  };

  const onSubmit = handleSubmit(async (data: FormFields) => {
    if (!data) {
      return;
    }

    if (state.activeField === "otp") {
      dispatch({ type: ActionType.OTP_SUBMIT });
      try {
        await auth.client.patch(BASE_URL + "/v2/identity/email/verify", {
          json: {
            nextEmail: data.email,
            code: data.otp,
            // user cookie exists, I Promise

            accessToken: sessionStorage.getItem("at"),
          },
        });
        // sleep
        await new Promise((r) => setTimeout(r, 4000));
        dispatch({ type: ActionType.OTP_SUCCESS });
        updateEmail(data.email);
      } catch (error) {
        logError({ action: "PROFILE_EMAIL_OTP_VERIFY", error });
        dispatch({
          type: ActionType.OTP_ERROR,
          value: getErrorMessage(error),
        });
      }
    } else {
      dispatch({ type: ActionType.EMAIL_SUBMIT });
      const URL = BASE_URL + "/v2/identity/email/update";
      try {
        await auth.client.patch(URL, {
          json: {
            nextEmail: data.email,
            domain: window.location.hostname,
            // user cookie exists, I Promise

            accessToken: sessionStorage.getItem("at"),
          },
        });
        dispatch({ type: ActionType.EMAIL_SUCCESS });
      } catch (error) {
        logError({ action: "PROFILE_EMAIL_UPDATE", error });
        dispatch({
          type: ActionType.EMAIL_ERROR,
          value: getErrorMessage(error),
        });
      }
    }
  });

  return (
    <div className="max-w-lg">
      <h2 className={theme.heading2Class}>Edit My Email Address</h2>

      <form onSubmit={onSubmit} className="flex flex-col gap-6">
        {state.activeField === "email" ? (
          <>
            <p className="opacity-70">
              In order to keep your personal information safe we require
              verification. Enter your NEW email address and we will send a
              unique, time-sensitive, single-use password.
            </p>
            <Email
              formErrors={errors}
              formValues={formValues}
              register={register}
            />
          </>
        ) : null}

        {state.activeField === "otp" ? (
          <>
            <div className="flex pt-3">
              <div className="pulsating-circle">
                <EnvelopeSimpleIcon color="#527800" size="32" />
              </div>
              <div className="ml-3 flex-grow">
                <p className="ellipsis mb-4">
                  <span className="block font-bold text-lg">
                    We sent a single-use password to:
                  </span>
                  <span className="ellipsis kup-font-monospace block">
                    {formValues.email}
                  </span>
                </p>

                <p className="opacity-75 text-sm">
                  If you don&apos;t see an email please check your SPAM folder
                  and whitelist email from:{" "}
                  <span className="kup-font-monospace">
                    {theme.supportEmail}
                  </span>
                  . You can also{" "}
                  <button
                    className="m-0 p-0 underline"
                    onClick={reset}
                    type="button"
                  >
                    resend the email
                  </button>
                  .
                </p>
              </div>
            </div>
            <OTP
              formErrors={errors}
              formValues={formValues}
              register={register}
            />
          </>
        ) : null}

        {state.emailErrorMessage ? (
          <Toast variant="error">
            <ToastTitle>{state.emailErrorMessage}</ToastTitle>
            Please try again. If this error continues{" "}
            <a className="underline" href={`mailto: ${theme.supportEmail}`}>
              please contact us at {theme.supportEmail}
            </a>
            .
          </Toast>
        ) : null}

        {state.otpErrorMessage ? (
          <Toast variant="error">
            <ToastTitle>{state.otpErrorMessage}</ToastTitle>
            Please try again. If this error continues{" "}
            <a className="underline" href={`mailto: ${theme.supportEmail}`}>
              please contact us at {theme.supportEmail}
            </a>
            .
          </Toast>
        ) : null}

        <div className="flex gap-3 items-center">
          <button
            className={`${theme.buttonPrimaryClass} ${
              state.isLoading ? "cursor-wait opacity-50" : ""
            }`}
            disabled={state.isLoading}
            type="submit"
          >
            Save Change
          </button>
          {state.isLoading ? <Spinner /> : null}
          {state.success ? (
            <div>
              <Check animationCallback={reset} size={44} />
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
}
